import {
    Box,
    Flex,
    Text,
    HStack,
    Divider,
    Image,
    Avatar
} from "@chakra-ui/react";
import { BusIcon, CarIcon, BikeIcon, LucideBike, TruckIcon } from "lucide-react";
import ArrowImg from '../../../assets/icons/arrow.png';
import { getDriverInitials } from "./hikeTripCard";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

const cardsBorderRadii ="8px";

export interface Location {
    address: string;
    lat: number;
    lng: number;
}

export interface driver {
    id: string;
    name: string;
    email: string;
    photoURL: string;
    createdAt: string;
    role: string;
}

export interface Car {
    _id: string,
    driverId: string,
    name: string,
    type: string,
    seats: number,
    numberPlate: string,
    __v: number;
}

interface TripDetailsProps {
    from: Location;
    to: Location;
    date: string;
    driver: driver;
    seatsAvailable: number;
    priceMk: number;
    departTime?: string;
    arriveTime?: string;
    car?: Car;
}

const getCarTypeIcon = (carType: string | undefined, color: string) => {
    const iconSize = 20;

    switch (carType?.toLowerCase()) {
        case "bus":
            return <BusIcon size={iconSize} color={color} />;
        case "car":
            return <CarIcon size={iconSize} color={color} />;
        case "bike":
        case "motorbike":
            return <BikeIcon size={iconSize} color={color} />;
        case "motorcycle":
            return <LucideBike size={iconSize} color={color} />;
        case "pickup":
        case "truck":
            return <TruckIcon size={iconSize} color={color} />;
        default:
            return <CarIcon size={iconSize} color={color} />;
    }
};

const formatTime = (timeStr?: string) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(h, m);
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const TripDetailsPanel: React.FC<TripDetailsProps> = ({
    from,
    to,
    date,
    driver,
    seatsAvailable,
    priceMk,
    departTime,
    arriveTime,
    car
}) => {
    const { theme } = useCustomTheme();
    const accent = theme.brand?.electricBlue || theme.primary;

    return (
        <Flex
            borderWidth="1px"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 5 }}
            bg={theme.surface}
            boxShadow={theme.shadowSm}
            gap={6}
            justify="space-between"
            direction={{ base: "column", md: "row" }}
            alignItems={{ base: "flex-start", md: "center" }}
        >
            {/* Driver + Car Type */}
            <Flex
                direction="column"
                minW={{ base: "100%", md: "220px" }}
                p={3}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                border="1px solid"
                borderColor={theme.border}
            >
                <Flex gap={3} alignItems="center" flexWrap="wrap">
                    <Text fontWeight="medium" fontSize="sm" color={theme.textSubtle}>
                        Driver:
                    </Text>

                    <Flex gap={2} alignItems="center">
                        {driver?.photoURL ? (
                            <Avatar size="sm" src={driver?.photoURL} name={driver.name} />
                        ) : (
                            <Avatar size="sm" name={driver?.name} bg="teal.500">
                                {getDriverInitials(driver?.name)}
                            </Avatar>
                        )}

                        <Text fontWeight="bold" color={theme.text}>
                            {driver?.name}
                        </Text>
                    </Flex>
                </Flex>

                <HStack mt={2} align="center" spacing={2}>
                    {getCarTypeIcon(car?.type, theme.textMuted)}
                    <Flex gap={2} flexWrap="wrap">
                        <Text color={theme.textSubtle} fontWeight="medium" textTransform="capitalize">
                            {car?.type || "-"}
                        </Text>
                        <Text color={theme.textMuted}>•</Text>
                        <Text color={theme.textSubtle} fontWeight="medium">
                            {car?.numberPlate || "-"}
                        </Text>
                    </Flex>
                </HStack>
            </Flex>

            {/* From → To */}
            <Flex
                align="center"
                gap={4}
                flex={1}
                justify="space-around"
                w="100%"
                p={3}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                border="1px solid"
                borderColor={theme.border}
            >
                <Flex direction="column" minW={0}>
                    <Text fontWeight="700" fontSize="lg" color={accent}>
                        {formatTime(departTime)}
                    </Text>
                    <Text fontWeight="600" fontSize="sm" color={theme.textSubtle} noOfLines={2}>
                        {from.address}
                    </Text>
                </Flex>

                <Image
                    w="40px"
                    src={ArrowImg}
                    alt="Arrow Right"
                    opacity={0.9}
                    filter={theme.name === "dark" ? "brightness(0.9)" : "none"}
                />

                <Flex direction="column" minW={0}>
                    <Text fontWeight="700" fontSize="lg" color={accent}>
                        {formatTime(arriveTime)}
                    </Text>
                    <Text fontSize="sm" fontWeight="600" color={theme.textSubtle} noOfLines={2}>
                        {to.address}
                    </Text>
                </Flex>
            </Flex>

            {/* Extra Details */}
            <Flex
                direction="column"
                align={{ base: "flex-start", md: "flex-end" }}
                minW={{ base: "100%", md: "220px" }}
                p={3}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                border="1px solid"
                borderColor={theme.border}
            >
                <Flex gap={2} alignItems="center" flexWrap="wrap">
                    <Text fontSize="sm" color={theme.textMuted}>
                        Date:
                    </Text>
                    <Text fontWeight="medium" color={theme.text}>
                        {formatDate(date)}
                    </Text>
                </Flex>

                <Divider my={2} borderColor={theme.divider} />

                <HStack spacing={6}>
                    <Box>
                        <Text fontSize="sm" color={theme.textMuted}>
                            Seats Left
                        </Text>
                        <Text
                            fontWeight="bold"
                            color={seatsAvailable > 0 ? theme.text : "red.500"}
                        >
                            {seatsAvailable}
                        </Text>
                    </Box>

                    <Box>
                        <Text fontSize="sm" color={theme.textMuted}>
                            Price (MK)
                        </Text>
                        <Text fontWeight="bold" color={theme.text}>
                            {priceMk.toLocaleString()}
                        </Text>
                    </Box>
                </HStack>
            </Flex>
        </Flex>
    );
};